/**
 * Date utilities for parsing backend UTC timestamps correctly.
 *
 * The backend sends ISO timestamps WITHOUT timezone suffix (e.g. "2026-02-24T13:00:00").
 * JavaScript's new Date() treats these as LOCAL time, causing wrong display for non-UTC users.
 * These helpers ensure timestamps are parsed as UTC and displayed in the user's local timezone.
 */

/**
 * Parse a backend ISO timestamp as UTC.
 * Appends 'Z' if no timezone indicator is present.
 */
export function parseUTC(dateStr) {
  if (!dateStr) return null
  // If it already has timezone info (Z, +, or - offset), parse as-is
  if (dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr)
  }
  // Append 'Z' to treat as UTC
  return new Date(dateStr + 'Z')
}

/**
 * Relative time display (e.g. "30s ago", "5m ago", "2h ago", "Jan 15")
 */
export function timeAgo(dateStr) {
  if (!dateStr) return ''
  const d = parseUTC(dateStr)
  if (!d || isNaN(d.getTime())) return ''
  const now = new Date()
  const diff = Math.floor((now - d) / 1000)

  if (diff < 0) return 'just now'
  if (diff < 10) return 'just now'
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 172800) return 'yesterday'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Format a date for display: "Feb 24, 2026"
 */
export function formatDate(dateStr) {
  if (!dateStr) return '—'
  const d = parseUTC(dateStr)
  if (!d || isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Format a datetime for display: "Feb 24, 2026, 6:30 PM"
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return '—'
  const d = parseUTC(dateStr)
  if (!d || isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

/**
 * Format time only: "6:30 PM"
 */
export function formatTime(dateStr) {
  if (!dateStr) return ''
  const d = parseUTC(dateStr)
  if (!d || isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
